import type { PropsWithChildren } from 'react';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';

import { useDirection } from '@/i18n/useDirection';
import { Toaster } from '@/components/Toast';
import { AuthProvider } from '@/contexts/AuthContext';
import { env } from '@/lib/env';

// Centralises global providers. AuthProvider exposes session + role/status
// to RequireRole and to layouts; it lives outside GoogleReCaptchaProvider
// because auth state must propagate to every page, including those that
// never use reCAPTCHA. Toaster is the in-house wrapper around sonner (UB25).
//
// The GoogleReCaptchaProvider is mounted only when env.recaptchaSiteKey
// is set. On a dev workstation without a key (per Spec V1.1 §M13.6 dev
// bypass), the children render without the provider; src/lib/
// useRecaptchaToken.ts handles that branch by returning null tokens.
// Mounting the provider with an empty key would inject a broken
// <script> tag and pollute the DOM.
export function AppProviders({ children }: PropsWithChildren) {
  useDirection();

  if (!env.recaptchaSiteKey) {
    return (
      <AuthProvider>
        {children}
        <Toaster />
      </AuthProvider>
    );
  }

  return (
    <AuthProvider>
      <GoogleReCaptchaProvider
        reCaptchaKey={env.recaptchaSiteKey}
        scriptProps={{ async: true, defer: true, appendTo: 'head' }}
        container={{ parameters: { badge: 'inline' } }}
      >
        {children}
        <Toaster />
      </GoogleReCaptchaProvider>
    </AuthProvider>
  );
}
